/// <amd-module name="Pipeline/Analytics/PipelineStepsDurationCard/ControllerViews/PipelineStepsDurationCard" />
import { ErrorComponent } from "Analytics/Shared/Analytics-Visuals-Common/ErrorComponent/ErrorComponent";
import { LoadingComponent } from "Analytics/Shared/Analytics-Visuals-Common/LoadingComponent/LoadingComponent";
import { IReportContext, Period, TrendBy } from "Analytics/Shared/Common/Types";
import { getPercentage } from "Analytics/Shared/Utils/Number";
import { ChartLegendComponent, IChartLegend, IChartLegendProps } from "Analytics/Shared/Visuals/ChartLegend/ChartLegend";
import { percentile_text } from "Pipeline/Analytics/Common/Constants";
import { colors } from "Pipeline/Analytics/Common/Constants";
import { maxItemsToShowInCharts } from "Pipeline/Analytics/Common/Constants";
import { AllStageName } from "Pipeline/Analytics/Common/Resources";
import { IStageMetrics } from "Pipeline/Analytics/Common/Types";
import { PipelineStepsDurationCardActionsCreator } from "Pipeline/Analytics/PipelineStepsDurationCard/Actions/PipelineStepsDurationCardActionsCreator";
import * as DurationUtils from "Pipeline/Analytics/PipelineStepsDurationCard/DurationUtils";
import * as Resources from "Pipeline/Analytics/PipelineStepsDurationCard/Resources";
import {
    IPipelineStepsDurationCardState,
    PipelineStepsDurationCardStore
} from "Pipeline/Analytics/PipelineStepsDurationCard/Stores/PipelineStepsDurationCardStore";
import * as React from "react";
import { localeFormat } from "VSS/Core/Util/String";
import { Chart } from "VSS/Features/Charts/Chart";
import { ChartProps } from "VSS/Features/Charts/Contracts";
import { DataPoint, TooltipLineItem } from "VSS/Features/Charts/Contracts";
import * as Charts_Contracts from "VSS/Features/Charts/Contracts";
import { css, IVssComponentContext, IVssComponentProperties, VssComponent } from "VSS/Platform/Layout";
import { Card } from "VSSUI/Card";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { IMenuItem } from "VSSUI/Menu";
import { SpinnerSize } from "VSSUI/Spinner";
import { getStackedBarChartProps } from "../Common/StackedBarChartDataHelper";
import { getStackedColumnChartProps, setCustomTooltipMapping } from "../Common/StackedColumnChartDataHelper";
import { IStageStepsDurationData, IStepDurationData } from "../Types";

export interface IPipelineStepsDurationCardProps extends IVssComponentProperties {
    store: PipelineStepsDurationCardStore;
    actionCreator: PipelineStepsDurationCardActionsCreator;
    reportContext: IReportContext;
    onDataRendered?: () => void;
    errorText?: string;
}

export class PipelineStepsDurationCard extends VssComponent<IPipelineStepsDurationCardProps, IPipelineStepsDurationCardState> {
    private _onDataRenderedCalled = false;

    constructor(props: IPipelineStepsDurationCardProps, context: IVssComponentContext) {
        super(props, context);
        this.state = {
            errorText: this.props.errorText
        } as Readonly<IPipelineStepsDurationCardState>;
    }

    public render() {
        if (this.state.data && this.state.data.length === 0) {
            return null;
        }
        return (
            <Card
                titleProps={{
                    text: Resources.CardTitle,
                    ariaLevel: 3
                }}
                className={this.props.className}
                headerCommandBarItems={this._getHeaderCommandBarItems()}
                headerDescriptionProps={{
                    text: localeFormat(Resources.CardSubTitle, percentile_text)
                }}
            >
                {this._getElementToDisplay()}
            </Card>
        );
    }

    public componentDidMount(): void {
        super.componentDidMount();
        if (this.props) {
            this.props.store.addChangedListener(this._onStoreUpdate);
            this.setState(this.props.store.getState());
            this.performOnDataRendered();
        }
    }

    public componentDidUpdate(prevProps: IPipelineStepsDurationCardProps, prevState: IPipelineStepsDurationCardState): void {
        super.componentDidUpdate(prevProps, prevState);
        this.performOnDataRendered();
    }

    public componentWillUnmount() {
        if (this.props) {
            this.props.store.removeChangedListener(this._onStoreUpdate);
        }
        super.componentWillUnmount();
    }

    private _getHeaderCommandBarItems = (): IHeaderCommandBarItem[] => {
        const commandBarItems: IHeaderCommandBarItem[] = [];
        const subMenuItems: IMenuItem[] = [];
        if (this.state.stages) {
            this.state.stages.forEach(stage => {
                subMenuItems.push({
                    id: stage.fullName,
                    text: stage.name,
                    onActivate: () => {
                        this._onStageChanged(stage);
                    }
                });
            });
        }
        if (subMenuItems.length > 1) {
            commandBarItems.push({
                id: "pipelineStagesCommandBarButton",
                text: this.state.selectedStage,
                important: true,
                subMenuProps: {
                    id: "stageSubMenu",
                    items: subMenuItems
                }
            });
        }
        return commandBarItems;
    };

    private performOnDataRendered() {
        if (this.state.data || this.state.errorText) {
            if (this.props.onDataRendered && !this._onDataRenderedCalled) {
                this.props.onDataRendered();
                this._onDataRenderedCalled = true;
            }
        }
    }

    private _onStageChanged = (stage: IStageMetrics): void => {
        this.props.actionCreator.selectStage(stage);
        if (this.state.confValues) {
            this.state.confValues.stage = stage;
        }
    };

    private _onStoreUpdate = () => {
        this.setState(this.props.store.getState());
    };

    private _getElementToDisplay(): JSX.Element {
        const topDurationSteps = this.state.topStepDurationData;
        const selectedStageName = this.state.selectedStage ? this.state.selectedStage : AllStageName;

        if (this.state.errorText) {
            return <ErrorComponent className="pipeline-steps-duration-container flex-grow" errorMessage={this.state.errorText} />;
        } else if (
            this.state.confValues != null &&
            this.state.data != null &&
            topDurationSteps &&
            topDurationSteps.stepDurationData[selectedStageName] != null
        ) {
            const normalizeChartData = DurationUtils.normalizeDurationData(this.state.data);
            const summaryChartProps = getStackedBarChartProps(this.context.pageContext, topDurationSteps, selectedStageName);
            const chartLegendProps = this.getLegendProps(topDurationSteps.stepDurationData[selectedStageName].stepsData);
            const topStepsDurationPercentage = this.getTopStepDurationPercentage(topDurationSteps, selectedStageName);
            const stackedColumnChartProps = getStackedColumnChartProps(
                this.context.pageContext,
                normalizeChartData.chartData,
                selectedStageName,
                topDurationSteps,
                this.state.confValues
            );
            this.normalizeTrendChartProps(stackedColumnChartProps, normalizeChartData.durationUnit);
            const durationDataCount = topDurationSteps.stepDurationData[selectedStageName].stepsData.length;

            return (
                <div className="pipeline-steps-duration-container flex-grow">
                    <div className="flex-row flex-wrap">
                        <div className="pipeline-failedruns-summary flex-column">
                            <ChartLegendComponent {...chartLegendProps} />
                            <div className="pipeline-step-duration-insight">
                                {localeFormat(
                                    Resources.InsightMessage,
                                    durationDataCount < maxItemsToShowInCharts ? durationDataCount : maxItemsToShowInCharts,
                                    topStepsDurationPercentage
                                )}
                            </div>
                            <div>
                                <Chart {...summaryChartProps} />
                            </div>
                        </div>
                        <div className="pipeline-step-duration-trend">
                            <Chart {...stackedColumnChartProps} />
                        </div>
                    </div>
                </div>
            );
        } else {
            return <LoadingComponent className="flex-column flex-grow" size={SpinnerSize.large} />;
        }
    }

    private getLegendProps(topStepsDuration: IStepDurationData[]): IChartLegendProps {
        const className = css("flex-grow");
        topStepsDuration = topStepsDuration.slice(0, maxItemsToShowInCharts);
        const legendRows = this.getLegendRows(topStepsDuration);

        return {
            className: className,
            legend: legendRows
        };
    }

    private getTopStepDurationPercentage(topStepsDuration: IStageStepsDurationData, selectedStageName: string): number {
        let totalStepDurationValue = 0;

        const selectedStageDurationData = topStepsDuration.stepDurationData[selectedStageName];
        const len = selectedStageDurationData.stepsData.length;
        if (len < maxItemsToShowInCharts) {
            return 100;
        }
        for (let i = 0; i < maxItemsToShowInCharts; i++) {
            totalStepDurationValue += selectedStageDurationData.stepsData[i].stepDuration;
        }

        return totalStepDurationValue <= selectedStageDurationData.totalStageStepDuration
            ? getPercentage(selectedStageDurationData.totalStageStepDuration, totalStepDurationValue)
            : 100;
    }

    private getLegendRows(topStepsDuration: IStepDurationData[]): IChartLegend[] {
        const i = 0;
        return topStepsDuration
            ? topStepsDuration
                  .filter(o => o.stepDuration !== 0)
                  .map((o, i) => {
                      return {
                          color: colors[i++],
                          label: o.stepName
                      } as IChartLegend;
                  })
            : [];
    }

    private normalizeTrendChartProps(trendChartProps: ChartProps, durationUnit: string) {
        if (trendChartProps.yAxis) {
            trendChartProps.yAxis.title = localeFormat(Resources.YAxisTitleFormat, trendChartProps.yAxis.title, durationUnit.toUpperCase());
        }
        trendChartProps = setCustomTooltipMapping(trendChartProps, (points: DataPoint[]): TooltipLineItem[] => {
            return points != null
                ? points.map(point => {
                      const item = {
                          styleType: {
                              color: point.color,
                              type: Charts_Contracts.TooltipLineItemMarkerType.Square
                          },
                          text: ""
                      } as Charts_Contracts.TooltipLineItem;
                      const seriesValue = point.values[0] != null ? Number(point.values[0]).toLocaleString(navigator.language) : [];

                      item.text = localeFormat(Resources.TooltipText, point.seriesName, seriesValue, durationUnit);
                      return item;
                  })
                : [];
        });
        return trendChartProps;
    }
}
